import Link from "next/link";
import Image from "next/image";
import useSWRImmutable from "swr/immutable";
import { useState, useEffect, useRef } from "react";
import ListPager from "./ListPager";
import { WidgetShare } from "./SocialButtons";
import { ProgressBarWidget } from "./ProgressBar";
import GenericMaleThumbnail from "../../public/images/thumbs-generic-male-1.png";
import GenericFemaleThumbnail from "../../public/images/thumbs-generic-female-1.png";
import RightArrow from "../../public/images/icons/angle-right.svg";
import styles from "../styles/SenatorialWidget.module.scss";
import {
  getTransmissionPercentage,
  getFriendlyFormattedDateTime,
} from "../helpers/util";

const cfUrl = process.env.API_CF_URL;
const basePath = process.env.SITE_URL;
const SenatorialWidget = ({
  view,
  regionList,
  senatorialList,
  subText,
  urlPath,
  url,
  precinctsLocal,
  columnsCount = 2,
  limit = 20,
  withResultsFooter = true,
  resetWrapper = false,
  locSlug = "philippines",
  isEmbed = false,
  isProvince = false,
}) => {
  const TOTAL_PRECINCTS_PH = regionList
    ? regionList
        .map((region) => (region || {}).precincts_count)
        .reduce((a, b) => a + b, 0)
    : null;
  const TOTAL_PRECINCTS_OAV = regionList
    ? (regionList.find((region) => region.slug === "oav") || {}).precincts_count
    : null;

  const pageSize = limit;
  const totalColumnSize =
    columnsCount === 2 ? styles.columnsCount_2 : styles.columnsCount_1;
  const _raceType = "SENATOR PHILIPPINES";
  const canvasReportCurrent =
    senatorialList?.results[0]?.contest?.canvass_report_certificates_current;
  const canvasReportTotal =
    senatorialList?.results[0]?.contest?.canvass_report_certificates_total;
  const canvasReportTimestamp =
    senatorialList?.results[0]?.contest?.canvass_report_timestamp;
  const listBody = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(limit);
  const [isMounted, setIsMounted] = useState(false);

  const loader = ({ src, width, quality }) => {
    return `${src}?w=${width}&q=${quality || 75}`;
  };
  const fetcher = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body,
    }).then(async (res) => {
      return res.json();
    });

  const handlePageClick = async (page) => {
    setStartIndex((page - 1) * pageSize);
    setEndIndex(page * pageSize);
    setCurrentPage(page);
    listBody.current.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { data: rawLatestData, error: latestResultsError } = useSWRImmutable(
    [
      `${cfUrl}/latestTransmissionCount`,
      JSON.stringify({
        data: {
          locationSlug:
            view !== "overseas-absentee-voting-results" ? locSlug : "oav",
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const latestResultsData = rawLatestData?.result;

  const { data: rawSenatorialData, error: senatorialError } = useSWRImmutable(
    [
      `${cfUrl}/latestLocationResults`,
      JSON.stringify({
        data: {
          locationSlug:
            view !== "overseas-absentee-voting-results" ? locSlug : "oav",
          raceType: _raceType,
          totalVoteCount: latestResultsData?.voteCount
            ? latestResultsData.voteCount
            : 0,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const senatorialData = rawSenatorialData?.result;

  senatorialList.results?.forEach((candidate) => {
    if (!senatorialData || senatorialData.length <= 0) {
      candidate.votePercentage = "0";
      candidate.voteCount = 0;
      return;
    }
    let item = senatorialData.find(
      (x) => x.candidateSlug.toLowerCase() === candidate.wp_slug.toLowerCase()
    );
    if (item === undefined || item === null) {
      candidate.votePercentage = "0";
      candidate.voteCount = 0;
    } else {
      candidate.votePercentage = item.votePercentage;
      candidate.voteCount = item.voteCount;
    }
  });

  const getPrecincts = () => {
    return {
      precinctPercentage:
        latestResultsData && latestResultsData.transmissionCount
          ? getTransmissionPercentage(
              latestResultsData.transmissionCount,
              view === "overseas-absentee-voting-results"
                ? TOTAL_PRECINCTS_OAV
                : view === "local-results"
                ? precinctsLocal
                : TOTAL_PRECINCTS_PH
            )
          : "0",
      timeStamp:
        latestResultsData && latestResultsData.timestamp
          ? getFriendlyFormattedDateTime(latestResultsData.timestamp)
          : getFriendlyFormattedDateTime(new Date().getTime()),
    };
  };

  const getShareMessage = () => {
    if (view === "official-results") {
      return "Official 2022 senatorial election results #PHVote #WeDecide #PHVoteResults";
    } else {
      return "Partial, unofficial 2022 senatorial election results #PHVote #WeDecide #PHVoteResults";
    }
  };

  const greenBars = (i) => {
    if (currentPage === 1 && i < 12) return styles.greenBars;
  };

  return (
    <div
      ref={listBody}
      className={
        !resetWrapper
          ? styles.senatorialContainer
          : `${styles.senatorialContainer} ${styles.resetWrapper}`
      }
    >
      <div className={styles.titleSubWrapper}>
        <h3 className={styles.titleSubWrapper_title}>SENATORIAL RACE</h3>
        {subText && <h5 className={styles.titleSubWrapper_sub}>{subText}</h5>}
      </div>
      {view === "default" ? (
        <p className={styles.desc}>
          The 12 persons with the most votes will join the 24-member Senate, the
          upper chamber of the bicameral Congress of the Philippines. They will
          serve for six years starting noon of June 30, 2022.{" "}
          <Link href="https://www.rappler.com/nation/elections/senator-philippines-powers-duties/">
            <a target="_blank">Read More...</a>
          </Link>
        </p>
      ) : (
        <>
          {view !== "official-results" ? (
            <p className={styles.desc}>
              <b>{getPrecincts().precinctPercentage}%</b> precincts reporting as
              of <b>{getPrecincts().timeStamp}</b>. Unofficial-partial results
              based on real-time data from the Comelec transparency server.{" "}
              {isProvince
                ? "Votes for national positions at provincial level include those for highly urbanized and independent component cities situated in the province."
                : ""}
              Based on real-time data from the Comelec media server.
            </p>
          ) : canvasReportCurrent === canvasReportTotal ? (
            <p className={styles.desc}>
              Final, official results based on <b>{canvasReportCurrent}</b>{" "}
              certificates of canvass.
            </p>
          ) : (
            <p className={styles.desc}>
              Partial, official results based on{" "}
              <b>
                {canvasReportCurrent} of {canvasReportTotal}
              </b>{" "}
              certificates of canvass as of <b>{canvasReportTimestamp}</b>.
            </p>
          )}
          <div className={styles.columns}>
            <div className={styles.columns_rankCandidates}>
              <span>Rank</span>
              <span>Candidates</span>
            </div>
            <div className={styles.columns_votesPercent}>
              <span>Votes</span>
              <span>Percent</span>
            </div>
          </div>
        </>
      )}

      <div
        className={
          view === "default"
            ? styles.candidateWrapper
            : `${styles.candidateWrapper} ${styles.resultCandidateWrapper} ${totalColumnSize}`
        }
      >
        <ol
          style={{
            gridTemplateRows: `repeat(${Math.ceil(
              senatorialList.results.slice(startIndex, endIndex).length / 2
            )}, 1fr)`,
          }}
        >
          {senatorialList &&
            senatorialList.results
              .sort((a, b) =>
                view !== "official-results"
                  ? b.voteCount - a.voteCount
                  : a.ranking - b.ranking
              )
              .map((items, i) => ({
                rank: i + 1,
                ...items,
              }))
              .slice(startIndex, endIndex)
              .map((candidate, i) => (
                <Link
                  view={view}
                  href={`${urlPath}${candidate?.wp_slug?.toLowerCase()}`}
                  key={i}
                  passHref
                >
                  <a target={isEmbed ? "_blank" : ""}>
                    <li>
                      <div
                        className={
                          view === "default"
                            ? styles.candidatesDetails
                            : `${styles.candidatesDetails} ${styles.resultCandidateDetails}`
                        }
                        data-num={
                          senatorialData && senatorialData.length
                            ? candidate.rank
                            : ""
                        }
                      >
                        <div
                          className={
                            view === "default"
                              ? styles.candidate
                              : `${styles.candidate} ${styles.resultCandidate}`
                          }
                        >
                          {view === "default" ? (
                            <Image
                              loader={loader}
                              src={
                                candidate.thumbnail
                                  ? candidate.thumbnail
                                  : (candidate.gender || "").toLowerCase() ===
                                    "female"
                                  ? GenericFemaleThumbnail
                                  : GenericMaleThumbnail
                              }
                              alt={candidate.raw_name}
                              layout="fixed"
                              width={70}
                              height={70}
                            />
                          ) : (
                            <div className={styles.imgWrapper}>
                              <Image
                                key={candidate.id}
                                priority={true}
                                loader={loader}
                                src={
                                  candidate.thumbnail
                                    ? candidate.thumbnail
                                    : GenericFemaleThumbnail
                                }
                                alt={candidate.raw_name}
                                layout="fixed"
                                width={70}
                                height={70}
                                // layout="fill"
                                // objectFit="cover"
                              />
                            </div>
                          )}
                          <div className={styles.detailsWrapper}>
                            <ul className={styles.details}>
                              {view === "default" && (
                                <li className={styles.id} />
                              )}
                              <h3
                                className={
                                  view === "default"
                                    ? styles.name
                                    : `${styles.name} ${styles.resultNameStyle}`
                                }
                              >
                                {candidate.last_name}
                                <span
                                  data-text={`${candidate?.raw_name
                                    ?.split(",")[1]
                                    .trim()
                                    .split("(")[0]
                                    .trim()
                                    .toLowerCase()} (${
                                    candidate.political_party
                                  })`}
                                />
                              </h3>
                              {view !== "default" && (
                                <div className={styles.result}>
                                  {view !== "official-results" ? (
                                    <span>
                                      {candidate?.voteCount?.toLocaleString()}{" "}
                                    </span>
                                  ) : (
                                    <span>
                                      {candidate?.result_turnout?.toLocaleString()}{" "}
                                    </span>
                                  )}
                                  {view !== "official-results" ? (
                                    <span>{candidate.votePercentage}%</span>
                                  ) : (
                                    <span>
                                      {getTransmissionPercentage(
                                        candidate?.result_turnout,
                                        senatorialList.results[0].contest
                                          .canvass_report_total_votes_count
                                      )}
                                      %
                                    </span>
                                  )}
                                </div>
                              )}
                            </ul>
                            {view !== "default" && (
                              <div className={styles.desktopProgressBar}>
                                {view !== "official-results" ? (
                                  <ProgressBarWidget
                                    color={styles.test}
                                    percentage={candidate.votePercentage}
                                  />
                                ) : (
                                  <ProgressBarWidget
                                    color={greenBars(i)}
                                    percentage={getTransmissionPercentage(
                                      candidate?.result_turnout,
                                      senatorialList.results[0].contest
                                        .canvass_report_total_votes_count
                                    )}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {view === "default" && (
                          <>
                            <div className={styles.arrow}>
                              <Image
                                loader={loader}
                                src={RightArrow}
                                alt="right arrow"
                                layout="fixed"
                                width={20}
                                height={20}
                              />
                            </div>
                            <div className={styles.line}>-</div>
                          </>
                        )}
                      </div>
                      {view !== "default" && (
                        <div className={styles.mobileProgressBar}>
                          {view !== "official-results" ? (
                            <ProgressBarWidget
                              percentage={candidate.votePercentage}
                            />
                          ) : (
                            <ProgressBarWidget
                              color={greenBars(i)}
                              percentage={getTransmissionPercentage(
                                candidate?.result_turnout,
                                senatorialList.results[0].contest
                                  .canvass_report_total_votes_count
                              )}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  </a>
                </Link>
              ))}
        </ol>
        {view !== "default" && (
          <div className={styles.resultsFooter}>
            {view !== "overseas-absentee-voting-results" &&
              withResultsFooter && (
                <h6>*Already includes Overseas Absentee Votes</h6>
              )}
            <ListPager
              totalCount={senatorialList.count}
              numberPerPage={pageSize}
              currentPage={currentPage}
              onPageClick={handlePageClick}
            />
          </div>
        )}
      </div>

      <WidgetShare
        loader={loader}
        url={url}
        fbQuote={getShareMessage()}
        tWQuoute={getShareMessage()}
        toolTipId={"toolTip_Default"}
        isMounted={isMounted}
        isEmbedShowed={!isEmbed}
        embedUrl={`${basePath}elections/2022/races/embed/${view}/senator`}
      />
    </div>
  );
};

export default SenatorialWidget;
